<template>
    <div class="bo-wrap h-full w-full flex flex-col text-left">
        <div class="bo-toolbar">
            <p class="bo-toolbar__hint">{{ entity }} · Library Settings</p>
            <div class="bo-toolbar__actions">
                <button
                    type="button"
                    class="bo-btn bo-btn--export"
                    :disabled="isExportExcel || loading || !record"
                    @click="generateReport"
                >
                    <font-awesome-icon :icon="['fas', 'file-excel']" class="bo-btn__icon bo-btn__icon--excel" />
                    <span>{{ isExportExcel ? "Exporting..." : "Export Excel" }}</span>
                </button>
                <button
                    v-if="canUpdate && !record"
                    type="button"
                    class="bo-btn bo-btn--edit"
                    :disabled="loading"
                    @click="openCreate"
                >
                    <font-awesome-icon icon="plus" class="bo-btn__icon" />
                    <span>Add</span>
                </button>
                <button
                    v-if="canUpdate && record"
                    type="button"
                    class="bo-btn bo-btn--edit"
                    :disabled="loading"
                    @click="openEdit"
                >
                    <font-awesome-icon icon="pen" class="bo-btn__icon" />
                    <span>Edit</span>
                </button>
            </div>
        </div>

        <div class="bo-body flex-auto flex flex-col">
            <div v-if="loading" class="flex-auto flex justify-center items-center py-24">
                <base-page-spinner type="spin" class-list="h-16"></base-page-spinner>
            </div>

            <div v-else-if="!record" class="flex-auto flex justify-center items-center py-20">
                <div class="bo-empty">
                    <div class="bo-empty__glyph">
                        <font-awesome-icon :icon="['fas', 'cog']" />
                    </div>
                    <h2 class="bo-empty__title">No library settings found</h2>
                    <p class="bo-empty__text">
                        No library settings record exists for this entity yet.
                    </p>
                    <button
                        v-if="canUpdate"
                        type="button"
                        class="bo-btn bo-btn--edit bo-empty__action"
                        @click="openCreate"
                    >
                        <font-awesome-icon icon="plus" class="bo-btn__icon" />
                        <span>Add library settings</span>
                    </button>
                </div>
            </div>

            <div v-else class="bo-content">
                <article class="bo-feature bo-feature--portal bo-anim" style="--bo-delay: 0ms">
                    <div class="bo-feature__icon" aria-hidden="true">
                        <font-awesome-icon :icon="['fas', 'globe']" />
                    </div>
                    <div class="bo-feature__copy min-w-0">
                        <p class="bo-feature__label">Portal</p>
                        <p class="bo-feature__value">{{ record.portal_label || "—" }}</p>
                        <p class="bo-feature__sub">{{ record.portal_url || "—" }}</p>
                    </div>
                </article>

                <div class="bo-metrics">
                    <article class="bo-feature bo-feature--asof bo-anim" style="--bo-delay: 60ms">
                        <div class="bo-feature__icon" aria-hidden="true">
                            <font-awesome-icon :icon="['fas', 'calendar-alt']" />
                        </div>
                        <div class="bo-feature__copy min-w-0">
                            <p class="bo-feature__label">As of</p>
                            <p class="bo-feature__value bo-feature__value--lg">
                                {{ record.as_of || "—" }}
                            </p>
                        </div>
                    </article>

                    <article class="bo-feature bo-feature--digital bo-anim" style="--bo-delay: 120ms">
                        <div class="bo-feature__icon" aria-hidden="true">
                            <font-awesome-icon :icon="['fas', 'database']" />
                        </div>
                        <div class="bo-feature__copy min-w-0">
                            <p class="bo-feature__label">Digital total</p>
                            <p class="bo-feature__value bo-feature__value--lg">
                                {{ formatNumber(record.digital_total) }}
                            </p>
                        </div>
                    </article>
                </div>

                <article class="bo-feature bo-feature--scope bo-anim" style="--bo-delay: 180ms">
                    <div class="bo-feature__icon" aria-hidden="true">
                        <font-awesome-icon :icon="['fas', 'layer-group']" />
                    </div>
                    <div class="bo-feature__copy min-w-0">
                        <p class="bo-feature__label">Digital scope</p>
                        <p class="bo-feature__value">{{ record.digital_scope || "—" }}</p>
                    </div>
                </article>
            </div>
        </div>

        <transition name="fade">
            <div
                v-if="showFormModal"
                class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4"
                @click.self="closeForm"
            >
                <div class="bo-form bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-screen overflow-y-auto">
                    <div class="bo-form__header">
                        <h3 class="text-lg font-semibold text-gray-900">
                            {{ isCreating ? "Add library settings" : "Edit library settings" }}
                        </h3>
                        <p class="text-sm text-gray-500 mt-1">{{ entity }}</p>
                    </div>

                    <div class="bo-form__body grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div class="sm:col-span-2">
                            <label class="bo-label">Portal URL</label>
                            <input v-model="form.portal_url" type="text" class="bo-input" placeholder="https://..." />
                        </div>
                        <div class="sm:col-span-2">
                            <label class="bo-label">Portal Label</label>
                            <input v-model="form.portal_label" type="text" class="bo-input" placeholder="ADPOLY Library Portal" />
                        </div>
                        <div>
                            <label class="bo-label">As of</label>
                            <input v-model="form.as_of" type="text" class="bo-input" placeholder="July 2026" />
                        </div>
                        <div>
                            <label class="bo-label">Digital Total</label>
                            <input v-model="form.digital_total" type="number" min="0" class="bo-input" placeholder="0" />
                        </div>
                        <div class="sm:col-span-2">
                            <label class="bo-label">Digital Scope</label>
                            <input v-model="form.digital_scope" type="text" class="bo-input" placeholder="ADPOLY AA & AD" />
                        </div>
                    </div>

                    <div class="bo-form__footer">
                        <button type="button" class="bo-btn bo-btn--ghost" @click="closeForm">Cancel</button>
                        <base-button
                            type="button"
                            :disabled="saving"
                            class="bg-amber-700 hover:bg-amber-800 text-white"
                            @click="saveForm"
                        >
                            {{ saving ? "Saving…" : "Save" }}
                        </base-button>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import gql from "graphql-tag";
import exportManagerMixin from "../../facility/modules/exportManagerMixin.js";

const SETTINGS_QUERY = gql`
    query LibrarySettings($entity: String!) {
        librarySettings(entity: $entity) {
            error
            error_code
            message
            data {
                id
                entity
                portal_url
                portal_label
                as_of
                digital_total
                digital_scope
                created_at
                updated_at
            }
        }
    }
`;

const CREATE_MUTATION = gql`
    mutation CreateLibrarySettings($input: LibrarySettingsInput!) {
        createLibrarySettings(input: $input) {
            error
            error_code
            message
            data {
                id
            }
        }
    }
`;

const UPDATE_MUTATION = gql`
    mutation UpdateLibrarySettings($id: ID!, $input: LibrarySettingsInput!) {
        updateLibrarySettings(id: $id, input: $input) {
            error
            error_code
            message
            data {
                id
            }
        }
    }
`;

function emptyForm() {
    return {
        portal_url: "",
        portal_label: "",
        as_of: "",
        digital_total: "",
        digital_scope: "",
    };
}

function parseOptionalInt(value) {
    if (value === "" || value === null || value === undefined) return null;
    var num = parseInt(value, 10);
    if (isNaN(num) || num < 0) return null;
    return num;
}

export default {
    name: "SettingsModule",
    mixins: [exportManagerMixin],
    props: {
        entity: { type: String, required: true },
        campus: { type: String, default: "" },
    },
    emits: ["changed"],
    data() {
        return {
            record: null,
            loading: false,
            isExportExcel: false,
            exportSingleRecord: true,
            showFormModal: false,
            isCreating: false,
            saving: false,
            form: emptyForm(),
        };
    },
    computed: {
        role() {
            return this.$store.getters.role;
        },
        permissions() {
            return this.$store.getters.permissions;
        },
        canIndex() {
            return this.hasPermission("library-settings-index");
        },
        canUpdate() {
            return this.hasPermission("library-settings-update");
        },
    },
    created() {
        this.loadRecord();
    },
    methods: {
        formatNumber(value) {
            if (value === null || value === undefined || value === "") return "—";
            return Number(value).toLocaleString();
        },
        hasPermission(name) {
            if (this.role === "superadmin") return true;
            return this.permissions && this.permissions.includes(name);
        },
        toastAlert(options) {
            if (!this.$swal) return Promise.resolve({});
            var merged = Object.assign({ timer: 2000 }, options);
            if (
                (merged.icon === "error" || merged.icon === "warning") &&
                (options.timer === undefined || options.timer === null)
            ) {
                merged.timer = 3500;
            }
            var self = this;
            var Toast = this.$swal.mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timerProgressBar: true,
                didOpen: function (toast) {
                    toast.addEventListener("mouseenter", self.$swal.stopTimer);
                    toast.addEventListener("mouseleave", self.$swal.resumeTimer);
                },
            });
            return Toast.fire(merged);
        },
        loadRecord() {
            var self = this;
            this.loading = true;
            this.$apollo
                .query({
                    query: SETTINGS_QUERY,
                    variables: { entity: this.entity },
                    fetchPolicy: "network-only",
                })
                .then(function (result) {
                    var data = result && result.data ? result.data : null;
                    var payload = data && data.librarySettings ? data.librarySettings : null;
                    if (payload && !payload.error) {
                        self.record = payload.data || null;
                    } else {
                        self.record = null;
                        self.toastAlert({
                            icon: "error",
                            title: (payload && payload.message) || "Failed to load library settings",
                        });
                    }
                })
                .catch(function (err) {
                    self.record = null;
                    self.toastAlert({
                        icon: "error",
                        title: (err && err.message) || "Failed to load library settings",
                    });
                })
                .finally(function () {
                    self.loading = false;
                });
        },
        openCreate() {
            if (this.record) return;
            this.isCreating = true;
            this.form = emptyForm();
            this.showFormModal = true;
        },
        openEdit() {
            if (!this.record) return;
            var item = this.record;
            this.isCreating = false;
            this.form = {
                portal_url: item.portal_url || "",
                portal_label: item.portal_label || "",
                as_of: item.as_of || "",
                digital_total:
                    item.digital_total === null || item.digital_total === undefined
                        ? ""
                        : item.digital_total,
                digital_scope: item.digital_scope || "",
            };
            this.showFormModal = true;
        },
        closeForm() {
            this.showFormModal = false;
            this.isCreating = false;
            this.form = emptyForm();
        },
        buildInput() {
            return {
                entity: this.entity,
                portal_url: this.form.portal_url ? String(this.form.portal_url).trim() : null,
                portal_label: this.form.portal_label ? String(this.form.portal_label).trim() : null,
                as_of: this.form.as_of ? String(this.form.as_of).trim() : null,
                digital_total: parseOptionalInt(this.form.digital_total),
                digital_scope: this.form.digital_scope
                    ? String(this.form.digital_scope).trim()
                    : null,
            };
        },
        saveForm() {
            var self = this;
            if (!this.isCreating && (!this.record || !this.record.id)) {
                this.toastAlert({ icon: "error", title: "No record selected to update" });
                return;
            }

            this.saving = true;
            var input = this.buildInput();
            var request = this.isCreating
                ? this.$apollo.mutate({
                      mutation: CREATE_MUTATION,
                      variables: { input: input },
                  })
                : this.$apollo.mutate({
                      mutation: UPDATE_MUTATION,
                      variables: { id: this.record.id, input: input },
                  });

            request
                .then(function (res) {
                    var data = res && res.data ? res.data : null;
                    var payload = self.isCreating
                        ? data && data.createLibrarySettings
                            ? data.createLibrarySettings
                            : null
                        : data && data.updateLibrarySettings
                          ? data.updateLibrarySettings
                          : null;

                    if (payload && !payload.error) {
                        self.toastAlert({
                            icon: "success",
                            title: payload.message || "Saved successfully",
                        });
                        self.closeForm();
                        self.loadRecord();
                        self.$emit("changed", { refresh: true });
                    } else {
                        self.toastAlert({
                            icon: "error",
                            title: (payload && payload.message) || "Save failed",
                        });
                    }
                })
                .catch(function (err) {
                    self.toastAlert({
                        icon: "error",
                        title: (err && err.message) || "Save failed",
                    });
                })
                .finally(function () {
                    self.saving = false;
                });
        },
        exportFileName: function () {
            return "ADPOLY_Library_Settings";
        },
        exportSheetName: function () {
            return "Settings";
        },
        buildExportRows: function () {
            if (!this.record) return [];
            return [
                {
                    "Portal URL": this.record.portal_url || "",
                    "Portal Label": this.record.portal_label || "",
                    "As Of": this.record.as_of || "",
                    "Digital Total":
                        this.record.digital_total === null ||
                        this.record.digital_total === undefined
                            ? ""
                            : this.record.digital_total,
                    "Digital Scope": this.record.digital_scope || "",
                },
            ];
        },
    },
};
</script>

<style lang="scss" scoped>
.bo-wrap {
    --bo-ink: #1c1917;
    --bo-muted: #78716c;
    --bo-line: #e7e5e4;
    --bo-amber: #b45309;
    --bo-amber-soft: #fff7ed;
    background:
        radial-gradient(1200px 420px at 10% -10%, rgba(251, 191, 36, 0.16), transparent 55%),
        radial-gradient(900px 380px at 100% 0%, rgba(15, 118, 110, 0.08), transparent 50%),
        linear-gradient(180deg, #fafaf9 0%, #ffffff 48%);
}

.bo-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 1.15rem 1.25rem 0.35rem;
}

@media (min-width: 640px) {
    .bo-toolbar {
        padding-left: 2rem;
        padding-right: 2rem;
    }
}

.bo-toolbar__hint {
    margin: 0;
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--bo-muted);
}

.bo-toolbar__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;
}

.bo-body {
    padding: 0.75rem 1.25rem 2rem;
}

@media (min-width: 640px) {
    .bo-body {
        padding-left: 2rem;
        padding-right: 2rem;
    }
}

.bo-content {
    max-width: 56rem;
    margin: 0 auto;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.bo-metrics {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;
}

@media (min-width: 640px) {
    .bo-metrics {
        grid-template-columns: 1fr 1fr;
    }
}

.bo-feature {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 1.25rem 1.35rem;
    border-radius: 1.15rem;
    border: 1px solid var(--bo-line);
    background: rgba(255, 255, 255, 0.9);
    box-shadow: 0 1px 2px rgba(28, 25, 23, 0.04);
}

.bo-feature__icon {
    flex-shrink: 0;
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 0.9rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.05rem;
    color: #fff;
}

.bo-feature--portal .bo-feature__icon {
    background: linear-gradient(145deg, #d97706, #b45309);
}

.bo-feature--asof .bo-feature__icon {
    background: linear-gradient(145deg, #0d9488, #0f766e);
}

.bo-feature--digital .bo-feature__icon {
    background: linear-gradient(145deg, #57534e, #44403c);
}

.bo-feature--scope .bo-feature__icon {
    background: linear-gradient(145deg, #ca8a04, #a16207);
}

.bo-feature__label {
    margin: 0;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--bo-muted);
}

.bo-feature__value {
    margin: 0.35rem 0 0;
    font-size: 1.05rem;
    font-weight: 650;
    line-height: 1.45;
    color: var(--bo-ink);
    word-break: break-word;
}

.bo-feature__value--lg {
    font-size: 1.55rem;
    font-weight: 750;
    letter-spacing: -0.02em;
    line-height: 1.2;
}

.bo-feature__sub {
    margin: 0.25rem 0 0;
    font-size: 0.85rem;
    color: var(--bo-muted);
    word-break: break-all;
}

.bo-anim {
    animation: boRise 0.45s ease both;
    animation-delay: var(--bo-delay, 0ms);
}

@keyframes boRise {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.bo-empty {
    text-align: center;
    max-width: 22rem;
    padding: 0 1rem;
}

.bo-empty__glyph {
    width: 3.5rem;
    height: 3.5rem;
    margin: 0 auto 1rem;
    border-radius: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bo-amber-soft);
    color: var(--bo-amber);
    font-size: 1.35rem;
}

.bo-empty__title {
    margin: 0 0 0.4rem;
    font-size: 1.15rem;
    font-weight: 700;
    color: var(--bo-ink);
}

.bo-empty__text {
    margin: 0;
    color: var(--bo-muted);
    font-size: 0.95rem;
    line-height: 1.5;
}

.bo-empty__action {
    margin-top: 1.25rem;
}

.bo-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    border-radius: 0.75rem;
    padding: 0.6rem 1rem;
    font-size: 0.875rem;
    font-weight: 600;
}

.bo-btn:disabled {
    pointer-events: none;
    opacity: 0.55;
}

.bo-btn__icon {
    font-size: 0.85rem;
}

.bo-btn__icon--excel {
    color: #059669;
}

.bo-btn--export {
    border: 1px solid rgba(16, 185, 129, 0.28);
    background: linear-gradient(to bottom, #ffffff, rgba(16, 185, 129, 0.08));
    color: #052e1f;
}

.bo-btn--edit {
    border: 1px solid transparent;
    background: linear-gradient(145deg, #d97706, #b45309);
    color: #fff;
}

.bo-btn--ghost {
    background: #ffffff;
    color: #44403c;
    border: 1px solid var(--bo-line);
}

.bo-input {
    width: 100%;
    border: 1px solid #d6d3d1;
    border-radius: 0.65rem;
    padding: 0.55rem 0.8rem;
    font-size: 0.875rem;
    background: #ffffff;
    color: var(--bo-ink);
}

.bo-input:focus {
    outline: none;
    border-color: #d97706;
    box-shadow: 0 0 0 3px rgba(217, 119, 6, 0.15);
}

.bo-form__header {
    padding: 1.25rem 1.5rem 0.85rem;
    border-bottom: 1px solid #f5f5f4;
}

.bo-form__body {
    padding: 1.25rem 1.5rem;
}

.bo-form__footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    padding: 1rem 1.5rem 1.25rem;
    border-top: 1px solid #f5f5f4;
}

.bo-label {
    display: block;
    font-size: 0.8rem;
    font-weight: 600;
    color: #44403c;
    margin-bottom: 0.35rem;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
